import type {
  Attachment,
  CriterionStatus,
  FileEvaluation,
  FileEvaluationCriterion,
} from "@nlc/shared-types";

import { AUTO_CHECK_SCORE_THRESHOLD } from "@nlc/shared-types";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Eye,
  FileIcon,
  ImageIcon,
  User,
} from "lucide-react";
import { twMerge } from "tailwind-merge";

import { Badge } from "@/components/badge";
import { Collapse } from "@/components/collapse";
import { useImagePreview } from "@/hooks/useImagePreview";
import { dateFormatterWithHour } from "@/utils/dateFormatterWithHour";

const CRITERION_LABELS: Record<string, string> = {
  brand_adherence: "Brand adherence",
  copy_compliance: "Copy compliance",
  file_type_format: "File type & format",
  size_dimensions: "Size & dimensions",
  spelling_grammar: "Spelling & grammar",
};

interface ConfidenceCheckerEvaluationCardProps {
  attachment: Attachment;
  designerName?: null | string;
  evaluation: FileEvaluation | null;
  isDelivered?: boolean;
  onPreview?: (attachmentId: string) => void;
}

export function ConfidenceCheckerEvaluationCard({
  attachment,
  designerName,
  evaluation,
  isDelivered,
  onPreview,
}: ConfidenceCheckerEvaluationCardProps) {
  const {
    hasFailed,
    onError: onImageError,
    src: imageSrc,
  } = useImagePreview({
    preferredVariant: "preview",
    url: attachment.url ?? undefined,
  });

  const score = evaluation?.overallScore ?? 0;
  const isImage =
    attachment.mime.startsWith("image/") ||
    Boolean(
      attachment.url &&
        /\.(png|jpe?g|webp|gif|svg|avif)($|\?)/i.test(attachment.url),
    );

  const canPreview = Boolean(attachment.url && !hasFailed && onPreview);

  const label = (
    <div className="flex min-w-0 flex-1 items-center gap-3 text-left">
      <div className="flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border-secondary bg-bg-primary">
        {isImage && attachment.url && !hasFailed ? (
          <img
            alt=""
            className="h-full w-full object-cover"
            onError={onImageError}
            src={imageSrc}
          />
        ) : isImage ? (
          <ImageIcon className="text-text-quaternary" size={16} />
        ) : (
          <FileIcon className="text-text-quaternary" size={16} />
        )}
      </div>

      <span
        className="min-w-0 flex-1 truncate text-sm font-semibold text-text-primary"
        title={attachment.name}
      >
        {attachment.name}
      </span>

      {isDelivered !== undefined &&
        (isDelivered ? (
          <Badge isRounded size="small" variant="success">
            Delivered
          </Badge>
        ) : (
          <Badge isRounded size="small" variant="secondary">
            Not delivered
          </Badge>
        ))}

      {evaluation ? (
        <span className={twMerge("text-sm font-bold", scoreColorClass(score))}>
          {score}%
        </span>
      ) : (
        <span className="text-xs font-medium text-text-quaternary">
          Not evaluated
        </span>
      )}

      {evaluation && (
        <EvaluationDisplayStatusBadge
          displayStatus={evaluation.displayStatus}
        />
      )}
    </div>
  );

  return (
    <Collapse label={label}>
      <div className="flex flex-col gap-5 md:flex-row">
        {/* Attachment preview */}
        <button
          aria-label={
            canPreview ? `Preview ${attachment.name}` : attachment.name
          }
          className={twMerge(
            "group relative flex aspect-[4/3] w-full shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border-secondary bg-bg-primary text-left transition-all md:w-64",
            canPreview
              ? "cursor-pointer hover:border-border-brand hover:shadow-xs"
              : "cursor-default",
          )}
          disabled={!canPreview}
          onClick={() => {
            if (canPreview && onPreview) {
              onPreview(String(attachment.id));
            }
          }}
          type="button"
        >
          {isImage && attachment.url && !hasFailed ? (
            <>
              <img
                alt={attachment.name}
                className="h-full w-full object-contain p-1 transition-transform duration-300 group-hover:scale-[1.02]"
                onError={onImageError}
                src={imageSrc}
              />
              {canPreview && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition-all duration-200 group-hover:bg-black/30 group-hover:opacity-100">
                  <div className="flex items-center gap-1.5 rounded-full bg-black/75 px-3 py-1.5 text-xs font-semibold text-white backdrop-blur-xs">
                    <Eye size={14} />
                    <span>Preview</span>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center gap-2 text-text-quaternary">
              {isImage ? <ImageIcon size={36} /> : <FileIcon size={36} />}
              <span className="text-xs font-medium text-text-tertiary">
                {attachment.mime || "File attachment"}
              </span>
            </div>
          )}
        </button>

        {/* Evaluation details */}
        <div className="flex flex-1 flex-col gap-4">
          {/* Score Bar */}
          {evaluation && (
            <div className="relative w-full">
              <div className="h-2 w-full overflow-hidden rounded-full bg-bg-secondary">
                <div
                  className={twMerge(
                    "h-full rounded-full transition-all duration-700",
                    scoreBarClass(score),
                  )}
                  style={{ width: `${Math.min(100, Math.max(0, score))}%` }}
                />
              </div>
              <div
                className="absolute top-1/2 h-3 w-0.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-border-primary"
                style={{ left: `${AUTO_CHECK_SCORE_THRESHOLD}%` }}
                title={`Auto-check threshold (${AUTO_CHECK_SCORE_THRESHOLD}%)`}
              />
            </div>
          )}

          {/* Diagnostic checks */}
          <div className="flex flex-col gap-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-text-quaternary">
              Diagnostic Checks
            </span>

            {evaluation && evaluation.criteria.length > 0 ? (
              <div className="flex flex-col gap-2">
                {evaluation.criteria.map((criterion) => (
                  <CriterionItem
                    criterion={criterion}
                    key={criterion.id || criterion.criterionKey}
                  />
                ))}
              </div>
            ) : (
              <p className="text-xs text-text-tertiary">
                No criteria checks available for this evaluation.
              </p>
            )}
          </div>

          <div className="mt-1 flex flex-wrap items-center justify-between gap-3 border-t border-border-secondary pt-3 text-xs text-text-quaternary">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              {evaluation?.createdAt ? (
                <div className="flex items-center gap-1.5 text-text-tertiary">
                  <Clock className="text-text-quaternary" size={14} />
                  <span>
                    Evaluated:{" "}
                    <span className="font-medium text-text-secondary">
                      {dateFormatterWithHour(evaluation.createdAt)}
                    </span>
                  </span>
                </div>
              ) : (
                <span className="text-text-quaternary">Not evaluated</span>
              )}

              {designerName && (
                <div className="flex items-center gap-1.5 text-text-tertiary">
                  <User className="text-text-quaternary" size={14} />
                  <span>
                    Designer:{" "}
                    <span className="font-medium text-text-secondary">
                      {designerName}
                    </span>
                  </span>
                </div>
              )}
            </div>

            {evaluation?.aiModelVersion && (
              <span className="text-[11px] text-text-quaternary">
                AI Model: {evaluation.aiModelVersion}
              </span>
            )}
          </div>
        </div>
      </div>
    </Collapse>
  );
}

function CriterionItem({ criterion }: { criterion: FileEvaluationCriterion }) {
  const { bgClass, borderClass, icon, textClass } =
    STATUS_CONFIG[criterion.status] ?? STATUS_CONFIG.pass;

  const label =
    CRITERION_LABELS[criterion.criterionKey] ?? criterion.criterionKey;

  return (
    <div
      className={twMerge(
        "flex items-start gap-3 rounded-lg border px-3.5 py-2.5",
        borderClass,
        bgClass,
      )}
    >
      <div className="mt-0.5 shrink-0">{icon}</div>
      <div className="flex flex-1 flex-col gap-0.5">
        <div className="flex items-center justify-between gap-2">
          <span className={twMerge("text-xs font-semibold", textClass)}>
            {label}
          </span>
          {criterion.score != null && (
            <span className="text-xs font-medium text-text-quaternary">
              {criterion.score}%
            </span>
          )}
        </div>
        {criterion.message && (
          <span className="text-xs text-text-secondary leading-relaxed">
            {criterion.message}
          </span>
        )}
      </div>
    </div>
  );
}

const STATUS_CONFIG = {
  fail: {
    bgClass: "bg-bg-error-secondary/20",
    borderClass: "border-border-error",
    icon: <AlertTriangle className="text-text-error-primary" size={15} />,
    textClass: "text-text-primary",
  },
  pass: {
    bgClass: "bg-bg-success-secondary/20",
    borderClass: "border-border-success",
    icon: <CheckCircle2 className="text-[#079455]" size={15} />,
    textClass: "text-text-primary",
  },
  warning: {
    bgClass: "bg-bg-warning-secondary/20",
    borderClass: "border-border-warning",
    icon: <AlertTriangle className="text-amber-500" size={15} />,
    textClass: "text-text-primary",
  },
} as const satisfies Record<
  CriterionStatus,
  {
    bgClass: string;
    borderClass: string;
    icon: React.ReactNode;
    textClass: string;
  }
>;

export function EvaluationDisplayStatusBadge({
  displayStatus,
}: {
  displayStatus: FileEvaluation["displayStatus"];
}) {
  if (displayStatus === "passed") {
    return (
      <Badge isRounded size="medium" variant="success">
        Auto-checked
      </Badge>
    );
  }
  if (displayStatus === "overridden") {
    return (
      <Badge isRounded size="medium" variant="secondary">
        Overridden
      </Badge>
    );
  }
  if (displayStatus === "needs_review") {
    return (
      <Badge isRounded size="medium" variant="warning">
        Needs Review
      </Badge>
    );
  }
  if (displayStatus === "failed") {
    return (
      <Badge isRounded size="medium" variant="error">
        AI Failed
      </Badge>
    );
  }

  return (
    <Badge isRounded size="medium" variant="blue">
      Loading...
    </Badge>
  );
}

function scoreColorClass(score: number): string {
  if (score >= 85) return "text-text-success-primary";
  if (score >= 65) return "text-[#F79009]";

  return "text-text-error-primary";
}

function scoreBarClass(score: number): string {
  if (score >= 85) return "bg-bg-success-solid";
  if (score >= 65) return "bg-[#F79009]";

  return "bg-bg-error-solid";
}
